import * as Api from '@ViewModels';
import { parseAsJson, useQueryState } from 'nuqs';
import React, { useMemo } from 'react';
import * as yup from 'yup';

const criteriaSchema = yup.array().of(
	yup.object({
		property: yup.string().optional(),
		op: yup.string().optional(),
		value: yup.string().optional(),
	})
);

const validateFilterCriteria = (value: unknown): Api.IInsurancePolicyFilterCriteria[] | null => {
	try {
		const validated = criteriaSchema.validateSync(value, { strict: false, abortEarly: false });
		return validated as unknown as Api.IInsurancePolicyFilterCriteria[];
	} catch {
		return null;
	}
};

const normalizeCriteria = (criteria: Api.IInsurancePolicyFilterCriteria[]): Api.IInsurancePolicyFilterCriteria[] => {
	return criteria.map(c => {
		const normalized: Api.IInsurancePolicyFilterCriteria = {};
		if (c.property) normalized.property = c.property;
		if (c.op) normalized.op = c.op;
		if (c.value) normalized.value = c.value;
		if (c.criteria && c.criteria.length > 0) {
			normalized.criteria = normalizeCriteria(c.criteria);
		}
		return normalized;
	});
};

/** Policy filter criteria kept in the URL under `urlKey`, as the filter bar reads and writes them. */
export const usePolicyFilterCriteria = (urlKey: string, defaultCriteria: Api.IInsurancePolicyFilterCriteria[]) => {
	const [filterCriteria, setFilterCriteria] = useQueryState(
		urlKey,
		parseAsJson<Api.IInsurancePolicyFilterCriteria[]>(validateFilterCriteria).withDefault(defaultCriteria)
	);

	const normalizedCriteria = useMemo(
		() => normalizeCriteria(filterCriteria || defaultCriteria),
		[filterCriteria, defaultCriteria]
	);
	const isUpdatingRef = React.useRef(false);
	const previousCriteriaStringRef = React.useRef<string>('');

	const handleFilterChange = React.useCallback(
		(newCriteria: Api.IInsurancePolicyFilterCriteria[]) => {
			if (isUpdatingRef.current) return;

			const normalized = normalizeCriteria(newCriteria);
			const criteriaString = JSON.stringify(normalized);

			if (criteriaString !== previousCriteriaStringRef.current) {
				isUpdatingRef.current = true;
				previousCriteriaStringRef.current = criteriaString;
				setFilterCriteria(normalized.length > 0 ? normalized : null);

				// Reset the flag after a short delay to allow URL update to complete
				setTimeout(() => {
					isUpdatingRef.current = false;
				}, 0);
			}
		},
		[setFilterCriteria]
	);

	React.useEffect(() => {
		const criteriaString = JSON.stringify(normalizedCriteria);
		if (criteriaString !== previousCriteriaStringRef.current && !isUpdatingRef.current) {
			previousCriteriaStringRef.current = criteriaString;
		}
	}, [normalizedCriteria]);

	return { normalizedCriteria, handleFilterChange } as const;
};
